import { config } from 'api';
import { clientCookies } from 'utils/cookies';
import { INITIAL_MAX_DISPLAY_FOPS, adjustFiltersStructure, addProductsPage } from 'apps/catalogue/actions/catalogue';
import { extractAndDeleteProductsFromSections } from 'apps/products/utils/extractAndDeleteProducts';
import { countProductsInSections } from 'apps/products/utils/countProducts';
import { addProducts } from 'apps/products/actions/products';
import { enqueueTaskByKey } from '../utils/enqueueTaskByKey';
import { requestLogin } from 'utils/loginAndRegistration/login';
import { noop } from 'utils/func';
import { isUserLoggedIn } from 'apps/user/helpers';
import { reportSubscribe } from '../utils/SubscriptionsAnalytics';
import { reportRecommendationsSubscribe } from '../utils/RecommendationsAnalytics';
import { setErrorMessage } from 'apps/notifications/actions/notifications';

import { NEVER } from 'apps/subscriptions/components/fopPartials/SubscriptionsFrequencyOptions';

export const SUBSCRIPTIONS_SHOW_INFOBOX = 'SUBSCRIPTIONS_SHOW_INFOBOX';
export const SUBSCRIPTIONS_HIDE_INFOBOX = 'SUBSCRIPTIONS_HIDE_INFOBOX';
export const SUBSCRIPTIONS_HIDE_TROLLEY_INFOBOX = 'SUBSCRIPTIONS_HIDE_TROLLEY_INFOBOX';
export const SUBSCRIPTIONS_ADD_SUBSCRIPTIONS_INFO = 'SUBSCRIPTIONS_ADD_SUBSCRIPTIONS_INFO';
export const SUBSCRIPTIONS_FETCH_ERROR = 'SUBSCRIPTIONS_FETCH_ERROR';
export const SUBSCRIPTIONS_SUBSCRIBE = 'SUBSCRIPTIONS_SUBSCRIBE';
export const SUBSCRIPTIONS_CHANGE_QUANTITY = 'SUBSCRIPTIONS_CHANGE_QUANTITY';
export const SUBSCRIPTIONS_DELETE = 'SUBSCRIPTIONS_DELETE';
export const SUBSCRIPTIONS_RESET = 'SUBSCRIPTIONS_RESET';
export const SUBSCRIPTIONS_WAIT_FOR_SUBSCRIPTION_CONFIRMATION = 'SUBSCRIPTIONS_WAIT_FOR_SUBSCRIPTION_CONFIRMATION';
export const SUBSCRIPTIONS_UPDATE_FREQUENCY = 'SUBSCRIPTIONS_UPDATE_FREQUENCY';
export const SUBSCRIPTIONS_CHANGE_FREQUENCY_IN_EDIT_MODE = 'SUBSCRIPTIONS_CHANGE_FREQUENCY_IN_EDIT_MODE';
export const SUBSCRIPTIONS_REMOVE_FREQUENCY_FROM_EDIT_MODE = 'SUBSCRIPTIONS_REMOVE_FREQUENCY_FROM_EDIT_MODE';
export const SUBSCRIPTIONS_SHOW_TROLLEY_INFOBOX = 'SUBSCRIPTIONS_SHOW_TROLLEY_INFOBOX';

export const hideInfoBox = () => ({
  type: SUBSCRIPTIONS_HIDE_INFOBOX,
});

export const hideTrolleyInfoBox = () => {
  clientCookies.set('subscriptions-show-trolley-infobox', false, { secure: true, httpOnly: false });
  return {
    type: SUBSCRIPTIONS_HIDE_TROLLEY_INFOBOX,
  };
};

export const showTrolleyInfoBox = () => {
  const cookieValue = clientCookies.get('subscriptions-show-trolley-infobox');
  const display = cookieValue ? !!JSON.parse(cookieValue) : true;
  return {
    type: display ? SUBSCRIPTIONS_SHOW_TROLLEY_INFOBOX : SUBSCRIPTIONS_HIDE_TROLLEY_INFOBOX,
  };
};

export const showInfoBox = () => ({
  type: SUBSCRIPTIONS_SHOW_INFOBOX,
});

// visible for tests
export const addSubscriptions = (subscriptionsBySku) => ({
  type: SUBSCRIPTIONS_ADD_SUBSCRIPTIONS_INFO,
  subscriptionsBySku,
});

export function findSubscriptionsFops(sections) {
  if (!sections) {
    return [];
  }

  const subscribedSection = sections
    .find(section => section.attributes && section.attributes.type === 'SUBSCRIPTIONS') || {};

  return subscribedSection.fops || [];
}

export function extractSubscriptionsBySku(sections) {
  const fops = findSubscriptionsFops(sections);

  const subscriptionsBySku = Object.assign({}, ...fops.map(fop => {
    const {
      sku,
      fopAttributes: {
        subscription_next_add_date: nextAddDate, subscription_quantity: quantity, subscription_frequency: frequency,
      },
    } = fop;

    return {
      [sku]: {
        quantity,
        frequency,
        nextAddDate,
      },
    };
  }));
  return subscriptionsBySku;
}

const createEnhancedFopCollection = (fopCollection) => {
  const enhancedFopCollection = { ...fopCollection };
  enhancedFopCollection.sections = enhancedFopCollection.sections || [];
  enhancedFopCollection.totalFops = countProductsInSections(enhancedFopCollection.sections);
  enhancedFopCollection.maxDisplayFops = enhancedFopCollection.totalFops === 0 ? 0 : INITIAL_MAX_DISPLAY_FOPS;
  return enhancedFopCollection;
};

const createEnhancedSubscriptionsPage = (subscriptionsPage) => {
  const enhancedFopCollection = createEnhancedFopCollection(subscriptionsPage.mainFopCollection);
  const enhancedSubscriptionsPage = { ...subscriptionsPage, mainFopCollection: enhancedFopCollection };
  return enhancedSubscriptionsPage;
};

export const fetchSubscriptionsPage = (route) => (dispatch, getState, { network }) => {
  const url = route.apiBasePaths.productsApi;
  return network.get(url).then(
    subscriptionsPage => {
      const enhancedSubscriptionsPage = createEnhancedSubscriptionsPage(subscriptionsPage);
      const { sections } = enhancedSubscriptionsPage.mainFopCollection;
      const products = extractAndDeleteProductsFromSections(sections);

      const subscriptionsBySku = extractSubscriptionsBySku(sections);
      dispatch(addProducts(products));

      dispatch(addSubscriptions(subscriptionsBySku));

      adjustFiltersStructure(enhancedSubscriptionsPage);
      dispatch(addProductsPage(route.toString(), enhancedSubscriptionsPage));
    },
    error => {
      dispatch({
        type: SUBSCRIPTIONS_FETCH_ERROR,
        // TODO SUBS waiting for designs
        errorMessage: `Something got wrong, please try again! Details: ${error.statusText}`,
      });
    },
  );
};

export const fetchSubscriptionForProduct = (sku) => (dispatch, getState, { network }) => {
  const url = `${config.apiBasePath}/subscriptions/${sku}`;
  return network.get(url).then(
    subscription => {
      dispatch(addSubscriptions({ [sku]: subscription }));
    },
    error => {
      dispatch({
        type: SUBSCRIPTIONS_FETCH_ERROR,
        // TODO SUBS waiting for designs
        errorMessage: `Something got wrong, please try again! Details: ${error.statusText}`,
      });
    }
  );
};

export const confirmSubscription = (sku, frequency, nextAddDate) => ({
  type: SUBSCRIPTIONS_SUBSCRIBE,
  sku,
  frequency,
  nextAddDate,
});

export const waitForSubscriptionConfirmation = sku => ({
  type: SUBSCRIPTIONS_WAIT_FOR_SUBSCRIPTION_CONFIRMATION,
  sku,
});

export const deleteSubscription = sku => ({
  type: SUBSCRIPTIONS_DELETE,
  sku,
});

export const resetSubscriptions = () => ({
  type: SUBSCRIPTIONS_RESET,
});

export const confirmSubscriptionFrequency = (sku, frequency, nextAddDate) => ({
  type: SUBSCRIPTIONS_UPDATE_FREQUENCY,
  sku,
  frequency,
  nextAddDate,
});

export const changeFrequencyInEditMode = (sku, frequency) => ({
  type: SUBSCRIPTIONS_CHANGE_FREQUENCY_IN_EDIT_MODE,
  sku,
  frequency,
});

export const removeFrequencyFromEditMode = sku => ({
  type: SUBSCRIPTIONS_REMOVE_FREQUENCY_FROM_EDIT_MODE,
  sku,
});

export const requestSubscribe = ({
  sku, frequency, collectionFopIdx, isRecommendation,
} = {}) => (dispatch, getState, { network, router }) => {
  if (!isUserLoggedIn(getState().user)) {
    requestLogin(router);
    return Promise.reject('User is not logged in')
      .then(noop, noop); // to avoid `Uncaught (in promise)...`
  }
  if (isRecommendation) {
    reportRecommendationsSubscribe(frequency, sku, collectionFopIdx);
  } else {
    reportSubscribe(frequency, sku);
  }

  dispatch(waitForSubscriptionConfirmation(sku));

  return network.post(`${config.apiBasePath}/subscriptions`, {
    body: JSON.stringify({
      quantity: 1,
      frequency,
      sku,
    }),
  }).then(response => {
    dispatch(confirmSubscription(sku, frequency, response.nextAddDate));
    dispatch(removeFrequencyFromEditMode(sku));
    if (Object.keys(getState().subscriptions.subscriptionsBySku).length === 1) {
      dispatch(showInfoBox());
    } else if (getState().subscriptions.showInfoBox === true) {
      dispatch(hideInfoBox()); // default state for infoBox.
    }
  }).catch(() => dispatch(deleteSubscription(sku)));
};

export const changeQuantity = (sku, quantity) => ({
  type: SUBSCRIPTIONS_CHANGE_QUANTITY,
  sku,
  quantity,
});

function requestDelete(sku, dispatch, network) {
  return network.del(`${config.apiBasePath}/subscriptions/${sku}`)
    .then(() => {
      dispatch(deleteSubscription(sku));
    });
}

export const requestReset = () => (dispatch, getState, { network }) =>
  network.del(`${config.apiBasePath}/subscriptions`)
    .then(() => {
      dispatch(resetSubscriptions());
    }).catch(() => {
      dispatch(setErrorMessage('There was a network problem, your Regulars has not been reset.'));
    });

export const requestFrequencyChange = (sku, frequency) => (dispatch, getState, { network }) => {
  const deferredRequest = () => (frequency !== NEVER ?
    network.patch(`${config.apiBasePath}/subscriptions/${sku}`, {
      body: JSON.stringify({
        frequency,
      }),
    }).then(response => {
      dispatch(confirmSubscriptionFrequency(sku, frequency, response.nextAddDate));
    }).catch(() => {
      dispatch(setErrorMessage('There was a network problem, your Regulars has not been reset.'));
    })
    :
    requestDelete(sku, dispatch, network)
  );
  return enqueueTaskByKey(deferredRequest, `subscriptions:${sku}`);
};

function requestQuantityChange({ sku, options, dispatch, quantity, network }) {
  return network.patch(`${config.apiBasePath}/subscriptions/${sku}`, options)
    .then(() => {
      dispatch(changeQuantity(sku, quantity));
    });
}

export const requestQuantityChangeOrDelete = (sku, quantityDiff) => (dispatch, getState, { network }) => {
  const deferredRequest = () => {
    const subscription = getState().subscriptions.subscriptionsBySku[sku];

    if (!subscription) {
      // This happens if the user has quantity 1, decreases it, which removes subscription, and immediately increases it back.
      // TODO: Optimistically updated store for Subscriptions, like in Catalogue, will fix that issue.
      return null;
    }

    const { quantity: oldQuantity } = subscription;
    const quantity = oldQuantity + quantityDiff;

    if (quantity === 0) {
      return requestDelete(sku, dispatch, network);
    } else {
      const options = {
        body: JSON.stringify({ quantity }),
      };
      return requestQuantityChange({ sku, options, dispatch, quantity, network });
    }
  };

  // The requests must be queued in synchronous manner, because backend api accepts only absolute value of quantity
  // instead of diff. Without it the previous request could overwrite the following one. We only synchronize request in
  // the same group to achieve max performance.
  return enqueueTaskByKey(deferredRequest, `subscriptions:${sku}`);
};
